import './ObjectsList.scss';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { useInjection } from 'inversify-react';
import _, { cloneDeep } from 'lodash';
import { transaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IAnnotationService } from '../../../../annotation.service';
import { FREE_DRAW_TOOLS, ISegmentation } from '../../../../annotations.interface';
import { IAnnotationsStore } from '../../../../annotations.store';
import { IAnnotationUiStore } from '../../../../annotationUi.store';
import { IFreeDrawSegmentationService } from '../../../../freeDrawSegmentation.service';
import { IQuestionModel } from '../../../../question.model';
import { ChangeSegmentationWithAnswersCommand } from '../../../../undoRedoCommands/changeSegmentationWithAnswersCommand';
import { IUndoRedoHistory } from '../../../../undoRedoHistory.service';
import { IAnnotationTypeBl } from '../../../annotationTypes/annotationType.bl';
import { IAnnotationTypeStore } from '../../../annotationTypes/annotationType.store';
import { AnnotationToolType } from '../../../annotationTypes/models/annotationToolType';
import { DeleteAnnotationTypeConfirmationModal } from './modals/DeleteAnnotationTypeConfirmationModal';
import { DeleteSegmentationConfirmationModal } from './modals/DeleteSegmentationConfirmationModal';
import { SegmentationRow } from './SegmentationRow';

export interface IProject {
  id: string;
  name: string;
  count: number;
  questions: IQuestionModel[];
}

interface IProps {
  isReadOnly: boolean;
  onClose?: () => void;
}

export interface IObjectListSegmentation extends ISegmentation {
  isVisible: boolean;
  isMarkingToolTypeVisible: boolean;
  name: string;
  order: number;
  isSelected: boolean;
  priority: number;
}

export const ObjectsListContainer = observer((props: IProps): ReactElement => {
  const annotationService = useInjection(IAnnotationService.$);
  const annotationUiStore = useInjection(IAnnotationUiStore.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const annotationTypeStore = useInjection(IAnnotationTypeStore.$);
  const undoRedoHistoryService = useInjection(IUndoRedoHistory.$);
  const freeDrawSegmentationService = useInjection(IFreeDrawSegmentationService.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);

  const { projectId } = useParams<{ projectId: string }>();
  const { t } = useTranslator();

  const [showDeleteAnnotationTypeModal, setShowDeleteAnnotationTypeModal] = useState(false);
  const [showDeleteSegmentationModal, setShowDeleteSegmentationModal] = useState(false);
  const [annotationTypeIdToDelete, setAnnotationTypeIdToDelete] = useState('');
  const [segmentationIdToDelete, setSegmentationIdToDelete] = useState('');

  const showDeleteAnnotationTypeModalHandler = (annotationTypeId: string): void => {
    setShowDeleteAnnotationTypeModal(true);
    setAnnotationTypeIdToDelete(annotationTypeId);
  };

  const hideDeleteAnnotationTypeModalHandler = (): void => {
    setShowDeleteAnnotationTypeModal(false);
    setAnnotationTypeIdToDelete('');
  };

  const handleConfirmDeleteAnnotationType = async (): Promise<void> => {
    const segmentations = annotationsStore.segmentations.filter((s) => s.feature.properties?.annotationTypeId !== annotationTypeIdToDelete);
    undoRedoHistoryService.addNewChangeStateCommand(segmentations);
    annotationService.deleteSegmentations(segmentations);
    hideDeleteAnnotationTypeModalHandler();
    return Promise.resolve();
  };

  const showDeleteSegmentationModalHandler = (segmentation: IObjectListSegmentation): void => {
    setShowDeleteSegmentationModal(true);
    setSegmentationIdToDelete(segmentation.feature.id);
  };

  const hideDeleteSegmentationModalHandler = (): void => {
    setShowDeleteSegmentationModal(false);
    setSegmentationIdToDelete('');
  };

  const handleConfirmDeleteSegmentation = async (): Promise<void> => {
    const segmentation = cloneDeep(annotationsStore.segmentations.find((s) => s.feature.id === segmentationIdToDelete));

    if (segmentation?.feature) {
      annotationService.deleteSegmentation(segmentation.feature.id);
    }
    annotationService.deselectSegmentation();
    freeDrawSegmentationService.clear();
    await annotationTypeBl.handleSelectedAnnotationDeleteInAnnotationTypeContainer();

    undoRedoHistoryService.addCommand(
      new ChangeSegmentationWithAnswersCommand(annotationService, annotationsStore, freeDrawSegmentationService, segmentation, undefined)
    );
    hideDeleteSegmentationModalHandler();
  };

  const handleHideShowAnnotationType = (markingToolId: string): void => {
    const options = annotationTypeStore.annotationTypesOptions.find((o) => o.id === markingToolId);
    if (options) {
      annotationTypeBl.setAnnotationTypeOptions(projectId || annotationsStore.projectId, { ...options, isVisible: !options.isVisible });
    }

    const featureIdsToShow = annotationsStore.segmentations
      .filter((s) => markingToolId === s.feature.properties?.annotationTypeId)
      .map((s) => ({ segmentationId: s.id, featureId: s.feature.id }));
    featureIdsToShow.forEach((s) => {
      annotationService.showSegmentation(s.featureId, s.segmentationId);
    });
  };

  const handleHideShowSegmentation = (segmentation: IObjectListSegmentation): void => {
    const segmentationIsVisible: boolean = segmentation.isMarkingToolTypeVisible && segmentation.isVisible;
    const markingToolIsVisible: boolean = segmentation.isMarkingToolTypeVisible;

    if (segmentationIsVisible) {
      annotationService.hideSegmentation(segmentation.feature.id, segmentation.id);

      const segmentationsCreatedWithSameTool = annotationsStore.segmentations.filter(
        (s) => s.feature.properties?.annotationTypeId === segmentation.feature.properties?.annotationTypeId
      );

      const allSegmentationsAreHidden = segmentationsCreatedWithSameTool.every((s) =>
        annotationsStore.hiddenSegmentations.some(
          (hiddenSegmentation) =>
            (hiddenSegmentation.segmentationId !== undefined && s.id !== undefined && hiddenSegmentation.segmentationId === s.id) ||
            (hiddenSegmentation.featureId !== undefined && s.feature.id !== undefined && hiddenSegmentation.featureId === s.feature.id)
        )
      );

      if (allSegmentationsAreHidden) {
        segmentationsCreatedWithSameTool.forEach((s) => {
          annotationService.showSegmentation(s.feature.id, s.id);
        });
        handleHideShowAnnotationType(segmentation.feature.properties?.annotationTypeId);
      }
    }

    if (!segmentationIsVisible) {
      annotationService.showSegmentation(segmentation.feature.id, segmentation.id);

      if (!markingToolIsVisible) {
        const options = annotationTypeStore.annotationTypesOptions.find((o) => o.id === segmentation.feature.properties?.annotationTypeId);
        if (options) {
          annotationTypeBl.setAnnotationTypeOptions(projectId || annotationsStore.projectId, { ...options, isVisible: true });
        }
        const segmentationsToHide = annotationsStore.segmentations
          .filter(
            (s) => s.feature.properties?.annotationTypeId === segmentation.feature.properties?.annotationTypeId && segmentation.feature.id !== s.feature.id
          )
          .map((s) => ({ segmentationId: s.id, featureId: s.feature.id }));

        annotationService.hideSegmentations(segmentationsToHide);
      }
    }
  };

  const handleSelectSegmentationAsync = async (segmentation: IObjectListSegmentation): Promise<void> => {
    if (props.isReadOnly) {
      annotationsStore.drawFocusFrameForId = segmentation.feature.id;
      annotationService.selectSegmentation(segmentation.feature.id);
      if (!segmentation.isVisible || !segmentation.isMarkingToolTypeVisible) {
        handleHideShowSegmentation(segmentation);
      }
      return;
    }

    if (await freeDrawSegmentationService.clearAsync()) {
      annotationService.deselectSegmentation();
      await annotationTypeBl.handleAnnotationTypeSelectedInAnnotationTypeContainer(AnnotationToolType.SELECT);

      const handleSelect = async (): Promise<void> => {
        annotationService.selectSegmentation(segmentation.feature.id);
        if (annotationsStore.selectedSegmentation) {
          annotationUiStore.setIsInValidation(false);

          if (FREE_DRAW_TOOLS.includes(annotationsStore.selectedSegmentation.feature.featureType)) {
            freeDrawSegmentationService.setFreeDrawFeature(annotationsStore.selectedSegmentation.feature);
            annotationsStore.drawFocusFrameForId = segmentation.feature.id;
            const previousAnnotationTypeId = annotationTypeStore.activeAnnotationTypeId;
            const annotationTypeId = annotationsStore.selectedSegmentation.feature.properties?.annotationTypeId;
            if (annotationTypeId !== previousAnnotationTypeId) {
              await annotationTypeBl.handleSegmentationClickedInSegmentationContainer(annotationTypeId).then((succeeded) => {
                if (succeeded && freeDrawSegmentationService.freeDrawFeature) {
                  freeDrawSegmentationService.selectFeature();
                }
              });
            }
          } else {
            freeDrawSegmentationService.clear();
            annotationsStore.drawFocusFrameForId = undefined;
          }
        }
      };

      transaction(() => void handleSelect());

      if (!segmentation.isVisible || !segmentation.isMarkingToolTypeVisible) {
        handleHideShowSegmentation(segmentation);
      }
    }
  };

  const updated: IObjectListSegmentation[] = _.map<ISegmentation, IObjectListSegmentation>(
    annotationsStore.segmentations,
    (segmentation: ISegmentation): IObjectListSegmentation => {
      const annotationType = annotationTypeStore.annotationTypes.find((at) => at.id === segmentation.feature.properties?.annotationTypeId);
      const visibilities = new Set<string | undefined>(annotationTypeStore.hiddenAnnotationTypes.flatMap((x) => x.annotationTypes));

      return {
        ...segmentation,
        isVisible: !annotationsStore.hiddenSegmentations.some(
          (hs) => (segmentation.id !== undefined && hs.segmentationId === segmentation.id) || hs.featureId === segmentation.feature.id
        ),
        isMarkingToolTypeVisible: !visibilities.has(annotationType?.id),
        name: annotationType?.name ?? '',
        order: annotationType?.order ?? 0,
        isSelected: annotationsStore.selectedSegmentation?.feature.id === segmentation.feature.id,
      };
    }
  );

  const ordered: IObjectListSegmentation[] = _.sortBy<IObjectListSegmentation>(
    updated,
    [
      function (s: IObjectListSegmentation): number {
        return s.order;
      },
    ],
    ['asc']
  );

  const grouped = _.groupBy(ordered, (s: ISegmentation) => s.feature.properties?.annotationTypeId);

  return (
    <div className='object-list'>
      <div>
        <div className='object-list-title'>
          <div>
            <FontAwesomeIcon icon={faBars} /> {t('list_of_objects')}
          </div>
          {props.onClose && (
            <IconButton onClick={props.onClose}>
              <FontAwesomeIcon icon={'times'} />
            </IconButton>
          )}
        </div>
        <div className='marking-tools-items-container'>
          {_.map(grouped, (segmentations: IObjectListSegmentation[], markingToolTypeId: string) => (
            <SegmentationRow
              isReadOnly={props.isReadOnly}
              segmentations={_.orderBy(segmentations, (s) => s.priority)}
              key={markingToolTypeId}
              onShowHideAnnotationTypeClick={handleHideShowAnnotationType}
              onShowHideSegmentationClick={handleHideShowSegmentation}
              onDeleteSegmentationClick={showDeleteSegmentationModalHandler}
              onDeleteAnnotationTypeClick={showDeleteAnnotationTypeModalHandler}
              onSegmentationClick={handleSelectSegmentationAsync}
            />
          ))}
        </div>
      </div>
      <DeleteAnnotationTypeConfirmationModal
        onAnnotationTypeDeleteCancel={hideDeleteAnnotationTypeModalHandler}
        showAnnotationTypeDeleteConfirmationModal={showDeleteAnnotationTypeModal}
        onAnnotationTypeDeleteConfirm={handleConfirmDeleteAnnotationType}
      />
      <DeleteSegmentationConfirmationModal
        onSegmentationDeleteCancel={hideDeleteSegmentationModalHandler}
        showSegmentationDeleteConfirmationModal={showDeleteSegmentationModal}
        onSegmentationDeleteConfirm={handleConfirmDeleteSegmentation}
      />
    </div>
  );
});
